import _ from 'lodash';
import { useQuery, QueryResult } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useObserver } from 'mobx-react';

import useCart from 'hooks/use-cart';
import useUser from 'src/hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { GetSpecialMenuCardsDocument, GetSpecialMenuCardsQuery, SpecialMenuCard } from 'src/gql/graphql';
import { specialHasRequiredProducts } from 'shared/helpers/specials';
import { toUppercaseMenuType } from 'shared/constants';
import { useStores } from 'contexts/stores';
import { determinePlatformType } from 'shared/helpers/enum-mappings';

type UseSpecialMenuCardsProps = {
  specialId?: string;
  filteredProducts?: unknown[];
  skip?: boolean;
};

type UseSpecialMenuCardsResult = Omit<QueryResult<GetSpecialMenuCardsQuery>, 'data' | 'error' | 'loading'> & {
  specials: SpecialMenuCard[];
  hasSpecials: boolean;
  loading: boolean;
  error: unknown;
};

/**
 * Hook to fetch simplified special menu card data
 * This is a lighter alternative to useDispensarySpecials when only menu display data is needed
 *
 * @param {Object} [props] - Optional configuration object
 * @param {string} [props.specialId] - Specific special ID to fetch
 * @param {Array} [props.filteredProducts] - Products to filter specials against
 * @param {boolean} [props.skip] - Skip the query
 * @returns {Object} { specials, hasSpecials, loading, error }
 */
export default function useSpecialMenuCards(props: UseSpecialMenuCardsProps = {}): UseSpecialMenuCardsResult {
  const dispensaryHookData = useDispensary();
  // These are used to account for the various places where the hook could be called from
  const { UI } = useStores();
  const uiDispensary = useObserver(() => UI?.dispensary);

  const cart = useCart();
  const cartDispensary = useObserver(() => cart.dispensary);
  const platformType = determinePlatformType(UI);

  const dispensary = dispensaryHookData.dispensary ?? uiDispensary ?? cartDispensary;
  const menuType = useObserver(() => cart.menuType);
  const flags = useFlags();

  // Get user ID for customer segment filtering
  const user = useUser();
  const userId = useObserver(() => user?.id);

  const { filteredProducts, specialId, skip: skipQuery = false } = props;
  const hideProductlessSpecials = flags['eng-38623.hide-product-less-specials'] as boolean | undefined;

  const variables = {
    dispensaryId: dispensary?.id ?? '',
    menuType: toUppercaseMenuType(menuType),
    platformType,
    ...(specialId && { specialId }),
    ...(userId && { ecomUserId: userId }),
  };

  const { data, loading, error, ...apolloValues } = useQuery(GetSpecialMenuCardsDocument, {
    skip: !dispensary || skipQuery,
    variables,
  });

  const cards = data?.getSpecialMenuCards?.menuCards ?? [];

  // Filter by product availability if needed
  const filteredMenuCards = hideProductlessSpecials
    ? cards.filter((special) => specialHasRequiredProducts(special, filteredProducts))
    : cards;

  // Response is already sorted by displayRank from backend
  const hasSpecials = filteredMenuCards.length > 0;

  return {
    specials: filteredMenuCards,
    hasSpecials,
    loading,
    error,
    ...apolloValues,
  };
}
